import { useState, useCallback, useRef } from 'react';
import { Report } from '../../../swimlane/lib/types';
import { CompanyAnalysisService, AnalysePayload, type AnalysisScopePayload } from '../../../services/api/companyAnalysisService';
import { iMap } from '../../../components/type';
/**
 * Hook pour gérer les rapports d'analyse company-wide
 * Gère l'envoi de prompts, le chargement de rapports existants, etc.
 */

interface UseCompanyReportsProps {
    accessToken: string;
    userId: string;
}

const COLLECT_ONLY_MAP_ID = '__collect_only__';

function realMapIdsFromReports(reports: Report[]): string[] {
    return [...new Set(reports.map(r => r.map_id).filter(id => id && id !== COLLECT_ONLY_MAP_ID))];
}

export const useCompanyReports = ({ accessToken, userId }: UseCompanyReportsProps) => {
    const [reports, setReports] = useState<Report[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    // ✅ useRef pour garder l'analysis_id persistant entre les renders
    const currentAnalysisIdRef = useRef<string>("");

    /**
     * Génère un ID temporaire pour les rapports en attente
     */
    const generateTempId = useCallback(() => {
        return `temp-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
    }, []);

    /**
     * Soumet un prompt d'analyse
     * Uploade également les fichiers uploadés s'il y en a
     */
    const submitPrompt = useCallback(async (
        promptText: string,
        selectedMaps: iMap[],
        analysisId: string,
        selectedFile?: File | File[] | null,
        collectionIds?: string[],
        options?: {
            linkedProcessId?: string | null;
            synthesisOnly?: boolean;
        },
    ): Promise<{ 
        success: boolean; 
        newAnalysisId?: string;
        insufficientCredits?: boolean;
        creditsData?: {
            credits_required: number;
            credits_available: number;
            deficit: number;
            plan?: string;
        };
    }> => {
        if (!promptText.trim()) {
            console.warn('Veuillez entrer un prompt');
            return { success: false };
        }

        // Cartographie requise sauf mode collecte seule (au moins une collecte)
        const hasCollections = (collectionIds?.filter(Boolean).length ?? 0) > 0;
        const hasMaps = selectedMaps && selectedMaps.length > 0;
        if (!hasMaps && !hasCollections) {
            console.error('Veuillez sélectionner au moins une cartographie ou une collecte');
            return { success: false };
        }

        const tempId = generateTempId();
        const wasEmpty = reports.length === 0;
        const selectedMapIds = hasMaps ? selectedMaps.map(m => m._id) : [];
        
        // Uploader les fichiers si présents (process_id requis par l'API file)
        const uploadedFileIds: string[] = [];
        if (selectedFile) {
            try {
                const { FamlaServices } = await import('../../../services');
                const processId = selectedMaps[0]?.process?._id ?? options?.linkedProcessId ?? null;
                
                if (processId) {
                    // Utiliser currentAnalysisIdRef.current ou analysisId ou DEFAULT
                    const currentId = currentAnalysisIdRef.current || analysisId;
                    const effectiveAnalysisId = (wasEmpty && !currentId) ? "DEFAULT" : currentId;
                    
                    // Si selectedFile est un tableau (nouveau format), uploader tous les fichiers
                    const filesToUpload = Array.isArray(selectedFile) ? selectedFile : [selectedFile];
                    
                    // Uploader chaque fichier séparément et récupérer son ID
                    for (const file of filesToUpload) {
                        const endpoint = `/file/new/upload/analyze?process_id=${processId}&analyse_id=${effectiveAnalysisId}`;
                        const formData = new FormData();
                        formData.append("fileobject", file);
                        
                        const uploadResponse = await FamlaServices("api_host", endpoint, "POST", formData, accessToken);
                        
                        // ✅ Récupérer l'ID du fichier depuis la réponse
                        if (uploadResponse.status === 200 || uploadResponse.status === 201) {
                            const uploadedFile = uploadResponse.body?.data;
                            if (uploadedFile && uploadedFile.id) {
                                uploadedFileIds.push(uploadedFile.id);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Erreur lors de l\'upload du fichier:', error);
                // Ne pas bloquer l'analyse si l'upload échoue
            }
        }

        // Ajouter un rapport temporaire
        const tempReport: Report = {
            _id: tempId,
            process_id: "",
            user_id: userId,
            map_id: selectedMapIds[0] || "",
            prompt: promptText,
            text: "",
            comment: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            name: "",
            analysis_id: analysisId,
            step: [1, 1],
        };

        setReports(prev => [...prev, tempReport]);
        setIsLoading(true);

        try {
            const payload: AnalysePayload = {
                text: promptText,
                map_list: selectedMapIds,
                file_ids: uploadedFileIds,
                collection_ids: collectionIds?.filter(Boolean) ?? [],
                synthesis_only: options?.synthesisOnly ?? !hasMaps,
            };

            const currentId = currentAnalysisIdRef.current || analysisId;
            const effectiveAnalysisId = (wasEmpty && !currentId) ? "DEFAULT" : currentId;

            let newReports: Awaited<ReturnType<typeof CompanyAnalysisService.submitAnalysis>>;

            if (hasMaps) {
                const firstMap = selectedMaps[0];
                if (!firstMap?.process?._id) {
                    throw new Error("Impossible de récupérer le process_id");
                }
                newReports = await CompanyAnalysisService.submitAnalysis(
                    firstMap.process._id,
                    firstMap._id,
                    effectiveAnalysisId,
                    payload,
                    accessToken,
                );
            } else {
                newReports = await CompanyAnalysisService.submitCollectAnalysis(
                    effectiveAnalysisId,
                    payload,
                    accessToken,
                );
            }

            // [AI CREDITS] Gérer le cas 402 Payment Required
            if (typeof newReports === 'object' && 'status' in newReports && newReports.status === 402) {
                // Supprimer le rapport temporaire
                setReports(prev => prev.filter(r => r._id !== tempId));
                // Retourner les données pour le modal
                return { 
                    success: false, 
                    insufficientCredits: true,
                    creditsData: newReports.data
                };
            }

            // Type guard pour vérifier que newReports est un Report ou Report[]
            if (typeof newReports === 'object' && 'status' in newReports) {
                // Ce cas ne devrait pas arriver ici, mais TypeScript a besoin de cette vérification
                return { success: false };
            }

            // Remplacer le rapport temporaire par les vrais rapports
            setReports(prev => {
                const filtered = prev.filter(r => r._id !== tempId);
                const normalized = Array.isArray(newReports) ? newReports : [newReports as Report];
                return [...filtered, ...normalized];
            });

            // ✅ Extraire l'analysis_id du rapport (pour premier rapport OU pour mise à jour)
            let newAnalysisId: string | undefined;
            if (Array.isArray(newReports) && newReports.length > 0) {
                newAnalysisId = newReports[0].analysis_id;
            } else if (!Array.isArray(newReports) && typeof newReports === 'object' && 'analysis_id' in newReports) {
                newAnalysisId = (newReports as Report).analysis_id;
            }
            
            // ✅ Mettre à jour le ref immédiatement pour garantir la persistance
            if (newAnalysisId) {
                currentAnalysisIdRef.current = newAnalysisId;
            }

            return { success: true, newAnalysisId };
        } catch (error: any) {
            // Supprimer le rapport temporaire
            setReports(prev => prev.filter(r => r._id !== tempId));
            console.error('Erreur lors de l\'envoi du prompt:', error);
            return { success: false };
        } finally {
            setIsLoading(false);
        }
    }, [reports.length, userId, accessToken, generateTempId]);

    /**
     * Charge une analyse existante (rapports + fichiers)
     */
    const loadAnalysis = useCallback(async (analysisId: string): Promise<{
        reports: boolean;
        loadedReports: Report[];
        files: any[];
        scope: AnalysisScopePayload | null;
        reportCount: number;
        reportMapIds: string[];
        reportCollectionIds: string[];
    }> => {
        setIsLoading(true);
        try {
            const [loadedReports, loadedFiles, scope] = await Promise.all([
                CompanyAnalysisService.getAnalysisReports(analysisId, accessToken),
                CompanyAnalysisService.getAnalysisFiles(analysisId, accessToken),
                CompanyAnalysisService.getAnalysisScope(analysisId, accessToken),
            ]);

            const reportMapIds = realMapIdsFromReports(loadedReports);
            const reportCollectionIds = [
                ...new Set(
                    loadedReports.flatMap(r => r.collection_ids || []).filter(Boolean),
                ),
            ];
            return {
                reports: loadedReports.length > 0,
                loadedReports,
                files: loadedFiles,
                scope,
                reportCount: loadedReports.length,
                reportMapIds,
                reportCollectionIds,
            };
        } catch (error: any) {
            console.error('Erreur lors du chargement de l\'analyse:', error);
            return {
                reports: false,
                loadedReports: [],
                files: [],
                scope: null,
                reportCount: 0,
                reportMapIds: [],
                reportCollectionIds: [],
            };
        } finally {
            setIsLoading(false);
        }
    }, [accessToken]);

    /**
     * Reset tous les rapports
     */
    const clearReports = useCallback(() => {
        setReports([]);
        // ✅ Réinitialiser le ref pour permettre une nouvelle analyse
        currentAnalysisIdRef.current = "";
    }, []);

    return {
        reports,
        setReports,
        isLoading,
        submitPrompt,
        loadAnalysis,
        clearReports,
    };
};

